/**
 * Bento number cards — scroll-triggered counters.
 *
 * @package HelloElementorChild
 */

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  animateCounter,
  killCounterFrames,
  parseCounterValue,
  prefersReducedMotion,
} from './camst-counter.js';
import { initCamstReveal, teardownCamstReveal } from './camst-reveal.js';

gsap.registerPlugin(ScrollTrigger);

var bentoInstances = new WeakMap();

function animateBentoCounters(root, reducedMotion) {
  root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
    if (counterEl.dataset.camstCounterDone === '1') {
      return;
    }

    counterEl.dataset.camstCounterDone = '1';
    animateCounter(counterEl, counterEl.getAttribute('data-value'), reducedMotion);
  });
}

/**
 * Non-numeric values are titles: show immediately, skip counter animation.
 *
 * @param {Element} counterEl
 * @return {boolean}
 */
function resolveStaticTitle(counterEl) {
  var value = counterEl.getAttribute('data-value');

  if (parseCounterValue(value)) {
    return false;
  }

  counterEl.textContent = value || '';
  counterEl.classList.add('camst-bento-numbers__value--title');
  counterEl.removeAttribute('data-camst-counter');
  delete counterEl.dataset.camstCounterDone;

  return true;
}

export function initCamstBentoNumbers(scope) {
  var roots = [];

  if (!scope) {
    roots = Array.prototype.slice.call(document.querySelectorAll('[data-camst-bento-numbers]'));
  } else if (scope.matches && scope.matches('[data-camst-bento-numbers]')) {
    roots = [scope];
  } else {
    roots = Array.prototype.slice.call(scope.querySelectorAll('[data-camst-bento-numbers]'));
  }

  roots.forEach(function (root) {
    if (bentoInstances.has(root)) {
      return;
    }

    var reducedMotion = prefersReducedMotion();
    var triggers = [];

    root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
      if (resolveStaticTitle(counterEl)) {
        return;
      }

      if (reducedMotion) {
        animateCounter(counterEl, counterEl.getAttribute('data-value'), true);
        return;
      }

      triggers.push(
        ScrollTrigger.create({
          trigger: counterEl,
          start: 'top 85%',
          once: true,
          onEnter: function () {
            animateCounter(counterEl, counterEl.getAttribute('data-value'), false);
          },
        })
      );
    });

    initCamstReveal(root);
    bentoInstances.set(root, { triggers: triggers });
  });
}

export function teardownCamstBentoNumbers(scope) {
  var roots = scope
    ? scope.matches && scope.matches('[data-camst-bento-numbers]')
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll('[data-camst-bento-numbers]'))
    : [];

  roots.forEach(function (root) {
    var instance = bentoInstances.get(root);

    if (instance && instance.triggers) {
      instance.triggers.forEach(function (trigger) {
        trigger.kill();
      });
    }

    killCounterFrames(root);
    root.querySelectorAll('[data-camst-counter]').forEach(function (counterEl) {
      delete counterEl.dataset.camstCounterDone;
    });

    teardownCamstReveal(root);
    bentoInstances.delete(root);
  });
}

export function refreshCamstBentoNumbers() {
  ScrollTrigger.refresh();
}
